<template>
  <v-container
    v-if="company"
    class="layout-container"
  >
    <BaseHeader
      title="MultiScale Nutzungs- und Lizenzbedingungen"
      :style-variant="10"
    >
      <template v-slot:design>
        <HeaderDesign />
      </template>

      <template v-slot:desc>
        <p>
          Alle Informationen rund um die Nutzungs- und Lizenzbedingungen an einem Ort.<br>
          Beiliegend finden Sie zudem die zugehörigen Anlagen:<br>
          <ul>
            <li>Anlage 1 der Nutzungs- und Lizenzbedingungen: Service Level Agreement</li>
            <li v-if="licenseAgreementHasAvv">
              Anlage 2 der Nutzungs- und Lizenzbedingungen: Vertrag zur Auftragsverarbeitung
            </li>
          </ul>
        </p>
      </template>
      <template v-slot:content>
        <v-breadcrumbs :items="breadcrumbItems" />
      </template>
    </BaseHeader>

    <v-card
      v-if="isContractOwner"
      outlined
      style="overflow:hidden"
    >
      <v-row>
        <v-col
          v-if="$vuetify.breakpoint.smAndUp"
          sm="3"
        >
          <div class="design-wrapper">
            <Leaf
              class="design-elements"
              width="1000"
              style="top:-1200px; right: 100px; opacity: 0.2"
              fill
            />

            <Leaf
              class="design-elements"
              width="60"
              style="top:10px; right: 150px; opacity: 0.8"
              stroke
              :stroke-width="1"
            />

            <Leaf
              class="design-elements"
              width="60"
              style="top:10px; right:130px; opacity: 0.3"
              fill
            />

            <SingleCircle
              class="design-elements"
              width="30px"
              style="top: 130px; right: 210px; opacity: 0.8"
              stroke
            />

            <SingleCircle
              class="design-elements"
              width="30px"
              style="top: 130px; right: 200px; opacity: 0.3"
              fill
            />
          </div>
        </v-col>
        <v-col
          cols="12"
          sm="9"
          class="pa-6"
        >
          <v-alert
            v-if="showUpdateWarning"
            type="warning"
          >
            Update: Es gab ein Update in den Nutzungs- und Lizenzbedingungen. Bitte lesen Sie die neuen Bedingungen und akzeptieren Sie diese, um die App weiter zu nutzen.
          </v-alert>

          <div class="d-flex align-center">
            <v-icon class="mr-2">
              mdi-file
            </v-icon>
            <a
              :href="licenseUrl"
              target="_blank"
              rel="noopener noreferrer"
              class="text-decoration-none"
            >
              <h4 class="pb-0 mb-0 ">
                MultiScale Lizenzvertrag
                <v-icon
                  color="primary"
                  size="14"
                >
                  mdi-open-in-new
                </v-icon>
              </h4>
            </a>
          </div>

          <div
            v-if="showDefaultText"
            class="pt-2"
          >
            <span>
              Bitte lesen Sie den MultiScale Lizenzvertrag.
              Nach Ihrer Zustimmung können Sie die App sofort nutzen.
            </span>
          </div>

          <div class="pt-2">
            <v-checkbox
              v-model="isAccept"
              :disabled="!canAccept"
              label="Ich habe den Lizenzvertrag gelesen und akzeptiere"
            />
            <p
              v-if="company.licenseAgreement?.acceptedAt"
              class="d-flex align-center"
            >
              <v-icon class="mr-2">
                mdi-calendar
              </v-icon>
              Zuletzt akzeptiert am:  {{ formatDate(company.licenseAgreement.acceptedAt) }}
            </p>
          </div>

          <div
            v-if="canAccept"
            class="justify-end d-flex"
          >
            <v-btn
              class="primary"
              :disabled="!isAccept"
              @click="submit"
            >
              Speichern und weiter
            </v-btn>
          </div>
        </v-col>
      </v-row>
    </v-card>
    <v-card
      v-else
      outlined
      class="pa-8"
    >
      <div v-if="!isAccept">
        <p>Die Nutzungs- und Lizenzbedingungen samt Anlage 1 Service Level Agreement sowie Anlage 2 Vertrag zur Auftragsverarbeitung wurde von dem Vertragsinhaber Ihres Unternehmens <b class="red--text">noch nicht akzeptiert </b>. Das ist jedoch Voraussetzung für die Nutzung der {{ readablePlatformName }} App. Bitte wenden Sie sich an Ihren Broker.</p>
        <p><b>Das können Sie tun:</b> Erinnern Sie den Vertragsinhaber Ihres Unternehmens an die Akzeptanz der Nutzungs- und Lizenzbedingungen.</p>
      </div>
      <div v-else>
        <p>Die Nutzungs- und Lizenzbedingungen samt Anlage 1 Service Level Agreement sowie Anlage 2 Vertrag zur Auftragsverarbeitung wurde von dem Vertragsinhaber Ihres Unternehmens <b class="green--text">akzeptiert </b>.</p>
      </div>
    </v-card>
  </v-container>
</template>

<script>
import LICENSE_AGREEMENT from '@/queries/LicenseAgreement.gql'
import UPDATE_LICENSE_AGREEMENT from '@/queries/UpdateLicenseAgreement.gql'
import featureMixin from '@/mixins/feature'
import HeaderDesign from '@/modules/licenseAgreement/HeaderDesign.vue'
import BaseHeader from '@/components/BaseHeader.vue'
import Leaf from '@/components/design-elements/leaf.vue'
import SingleCircle from '@/components/design-elements/singleCircle.vue'
import brandingMixin from '@/mixins/branding'
import bus, { eventNames } from '@/lib/eventBus'

export default {
  components: { BaseHeader, Leaf, SingleCircle, HeaderDesign },
  mixins: [featureMixin, brandingMixin],
  data () {
    return {
      isAccept: false,
      canAccept: true
    }
  },
  computed: {
    isContractOwner () {
      return this.$auth.user.isContractOwner
    },
    licenseUrl () {
      return this.getFeature(this.featureNames.LICENSE_AGREEMENT).config.licenseUrl
    },
    showDefaultText () {
      return !this.company.licenseAgreement?.acceptedAt
    },
    showUpdateWarning () {
      return this.company.licenseAgreement.acceptedAt && this.updatedLicenseAgreementExists
    },
    updatedLicenseAgreementExists () {
      return new Date(this.company.licenseAgreement.acceptedAt).getTime() < new Date(this.getFeature(this.featureNames.LICENSE_AGREEMENT).config.lastModifiedAt).getTime()
    },
    licenseAgreementHasAvv () {
      return this.getFeature(this.featureNames.LICENSE_AGREEMENT).config.hasAvv
    },
    breadcrumbItems () {
      return [
        { text: 'Impressum & Rechtliches', to: { path: '/legal' }, disabled: false, exact: true },
        { text: 'Nutzungs- und Lizenzbedingungen' }
      ]
    }
  },
  methods: {
    async submit () {
      try {
        const { data: { updateLicenseAgreement } } = await this.$apollo.mutate({
          mutation: UPDATE_LICENSE_AGREEMENT,
          variables: {
            companyId: this.$auth.user.companyId
          },
          update: (store, { data: { updateLicenseAgreement } }) => {
            const data = store.readQuery({ query: LICENSE_AGREEMENT, variables: { id: this.$auth.user.companyId } })
            data.company.licenseAgreement.acceptedAt = updateLicenseAgreement
            store.writeQuery({ query: LICENSE_AGREEMENT, data, variables: { id: this.$auth.user.companyId } })
          }
        })

        bus.$emit(eventNames.SHOW_SNACKBAR, { color: 'success', text: 'Nutzungs- und Lizenzbedingungen akzeptiert' })

        this.updateAcceptUI(updateLicenseAgreement)
        this.$router.push('/dashboard')
      } catch (err) {
        bus.$emit(eventNames.SHOW_SNACKBAR, { color: 'error', text: 'Problem beim Akzeptieren' })
      }
    },

    updateAcceptUI (licenseAgreementAcceptedAt) {
      this.isAccept = !!(licenseAgreementAcceptedAt && !this.updatedLicenseAgreementExists)
      this.canAccept = !this.isAccept
    }
  },
  apollo: {
    company: {
      query: LICENSE_AGREEMENT,
      variables () {
        return {
          id: this.$auth.user.companyId
        }
      },
      result ({ data }) {
        if (!data) return
        this.updateAcceptUI(data.company.licenseAgreement.acceptedAt)
      },
      skip () {
        return this.$auth.loading || !this.$auth.isAuthenticated
      }
    }
  }
}
</script>
<style scoped>
.design-wrapper {
  position: relative;
  width:300px;
  height: 1px;
}

.design-elements{
  position: absolute;
  z-index: 1;
  pointer-events: none;
}
</style>
